const sectionChips = [
  "01 Philosophy",
  "02 Colour",
  "03 Type",
  "04 Components",
  "05 Iconography",
  "06 Dashboard",
  "07 References",
  "08 AI Chat",
  "09 Gradients",
  "10 Summary",
];

const paletteColumns = [
  ["bg-kin-green", "bg-kin-green-dark", "bg-kin-green-light"],
  ["bg-kin-gold", "bg-kin-gold-dark", "bg-kin-gold-light"],
  ["bg-kin-orange", "bg-kin-orange-light", "bg-kin-lavender"],
  ["bg-kin-black", "bg-kin-olive", "bg-kin-muted"],
];

const miniTrend = [58, 60, 61, 63, 64, 66, 65, 67, 69, 71, 70, 73];
const miniTrendSpark = [60, 62, 61, 64, 66, 68, 67, 69];
const miniRangeState = { low: 38, high: 86, current: 74 };

const trendChart = {
  width: 640,
  height: 108,
  padding: 14,
  min: 56,
  max: 75,
};

const miniBoardFrame = {
  width: 1120,
  height: 840,
};

function drawLine(values, width, height, padding, min, max) {
  const range = max - min || 1;

  return values
    .map((value, index) => {
      const x = padding + (index * (width - padding * 2)) / (values.length - 1);
      const y = padding + ((max - value) * (height - padding * 2)) / range;
      return `${index === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(" ");
}

function drawArea(values, width, height, padding, min, max) {
  const line = drawLine(values, width, height, padding, min, max);
  const firstX = padding;
  const lastX = width - padding;
  const bottomY = height - padding;

  return `${line} L ${lastX.toFixed(2)} ${bottomY.toFixed(2)} L ${firstX.toFixed(2)} ${bottomY.toFixed(2)} Z`;
}

function MiniGauge({ value }) {
  const clampedValue = Math.max(0, Math.min(100, value));
  const angle = clampedValue * 3.6;

  return (
    <div
      className="relative h-8 w-8 rounded-full"
      style={{
        background: `conic-gradient(color-mix(in srgb, var(--color-kin-green) 80%, transparent) ${angle}deg, color-mix(in srgb, var(--color-kin-muted) 15%, transparent) ${angle}deg)`,
      }}
    >
      <div className="absolute inset-1 rounded-full bg-kin-white/95" />
      <span className="absolute inset-0 flex items-center justify-center text-[8px] font-semibold text-kin-green-dark">
        {clampedValue}
      </span>
    </div>
  );
}

export default function MiniPage() {
  return (
    <section
      id="mini-page"
      className="kin-section-glow border-t border-kin-olive/10 px-6 py-16 scroll-mt-16 sm:px-8 gradient-wash"
    >
      <div className="mx-auto max-w-6xl">
        <p className="text-sm font-semibold tracking-[0.22em] uppercase text-kin-muted">
          10 - At a Glance
        </p>

        <div className="mt-6 max-w-3xl">
          <h2 className="font-serif text-4xl font-bold text-kin-black sm:text-5xl">
            Single frame summary
          </h2>
          <p className="mt-4 text-base leading-relaxed text-kin-body/90 sm:text-lg">
            One board that reflects the complete system, including dashboard, references, and AI
            conversation patterns.
          </p>
        </div>

        <div className="mt-12 overflow-x-auto pb-2">
          <div
            className="relative mx-auto overflow-hidden rounded-[28px] border border-kin-olive/16 bg-kin-white shadow-[0_36px_80px_-52px_rgba(0,0,0,0.15)]"
            style={{ width: miniBoardFrame.width, height: miniBoardFrame.height }}
          >
            <div
              className="absolute inset-0 bg-kin-sunrise p-6"
              style={{ width: miniBoardFrame.width, height: miniBoardFrame.height }}
            >
              {/* Main Board Base - Liquid Glass */}
              <div className="kin-surface flex h-full flex-col rounded-[24px] p-4">
                <header className="flex items-center justify-between rounded-[20px] border border-kin-sage/25 bg-[rgba(254,253,251,0.4)] px-4 py-3 shadow-[0_2px_8px_rgba(0,0,0,0.03)] backdrop-blur-xl">
                  <div>
                    <p className="font-serif text-2xl font-bold leading-none text-kin-black">Kin</p>
                    <p className="mt-1 text-[10px] font-semibold tracking-[0.2em] uppercase text-kin-muted">
                      Living Brandbook 2026
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full border border-kin-green/30 bg-kin-green/10 px-3 py-1 text-[10px] font-semibold text-kin-green-dark">
                      Production-grade
                    </span>
                    <span className="rounded-full border border-kin-lavender/30 bg-kin-lavender/10 px-3 py-1 text-[10px] font-semibold text-kin-lavender">
                      Health Product
                    </span>
                  </div>
                </header>

                <div className="mt-4 grid flex-1 grid-cols-12 gap-4 min-h-0">
                  <div className="col-span-8 flex min-h-0 flex-col gap-4">
                    <div className="grid grid-cols-3 gap-4">
                      <article className="kin-surface-strong rounded-[20px] p-4">
                        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
                          Essence
                        </p>
                        <p className="mt-2 font-serif text-xl font-bold leading-tight text-kin-black">
                          Their skills,
                          <br />
                          your health.
                        </p>
                        <p className="mt-2 text-xs text-kin-body/85">
                          Warm clarity with clinical trust.
                        </p>
                      </article>

                      <article className="kin-surface-strong rounded-[20px] p-4">
                        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
                          Palette
                        </p>
                        <div className="mt-2 grid grid-cols-4 gap-2">
                          {paletteColumns.map((column, index) => (
                            <div key={index} className="space-y-1">
                              {column.map((tone) => (
                                <div
                                  key={tone}
                                  className={`h-6 rounded-sm border border-kin-olive/10 ${tone}`}
                                />
                              ))}
                            </div>
                          ))}
                        </div>
                      </article>

                      <article className="kin-surface-strong rounded-[20px] p-4">
                        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
                          Typography
                        </p>
                        <p className="mt-2 font-serif text-lg font-bold text-kin-black">
                          Cormorant
                        </p>
                        <p className="text-xs font-semibold text-kin-dark">Manrope UI system</p>
                        <p className="mt-2 text-xs text-kin-body/85">
                          Serif for voice, sans for operation.
                        </p>
                      </article>
                    </div>

                    <article className="kin-surface-strong flex-1 rounded-[24px] p-4 min-h-0 flex flex-col">
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-kin-muted">
                          06 Dashboard & Data
                        </p>
                        <span className="rounded-full border border-kin-green/20 bg-kin-green/10 px-2 py-1 text-[10px] font-semibold text-kin-green-dark">
                          Health Trend + Labs
                        </span>
                      </div>

                      <div className="mt-4 rounded-[16px] border border-kin-sage/20 bg-[rgba(254,253,251,0.5)] p-3 shadow-sm backdrop-blur-md">
                        <svg
                          viewBox={`0 0 ${trendChart.width} ${trendChart.height}`}
                          className="h-24 w-full"
                          preserveAspectRatio="xMinYMid meet"
                        >
                          {[0, 1, 2, 3].map((tick) => {
                            const y =
                              trendChart.padding +
                              (tick * (trendChart.height - trendChart.padding * 2)) / 3;
                            return (
                              <line
                                key={tick}
                                x1={trendChart.padding}
                                x2={trendChart.width - trendChart.padding}
                                y1={y}
                                y2={y}
                                stroke="rgba(142,142,147,0.15)"
                                strokeWidth="1"
                              />
                            );
                          })}

                          <path
                            d={drawArea(
                              miniTrend,
                              trendChart.width,
                              trendChart.height,
                              trendChart.padding,
                              trendChart.min,
                              trendChart.max,
                            )}
                            fill="rgba(52,199,89,0.1)"
                          />
                          <path
                            d={drawLine(
                              miniTrend,
                              trendChart.width,
                              trendChart.height,
                              trendChart.padding,
                              trendChart.min,
                              trendChart.max,
                            )}
                            fill="none"
                            stroke="rgba(52,199,89,0.9)"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          {miniTrend.map((point, index) => {
                            const x =
                              trendChart.padding +
                              (index * (trendChart.width - trendChart.padding * 2)) /
                                (miniTrend.length - 1);
                            const y =
                              trendChart.padding +
                              ((trendChart.max - point) *
                                (trendChart.height - trendChart.padding * 2)) /
                                (trendChart.max - trendChart.min);
                            return (
                              <circle
                                key={index}
                                cx={x}
                                cy={y}
                                r="3"
                                fill="rgba(52,199,89,1)"
                                stroke="white"
                                strokeWidth="1"
                              />
                            );
                          })}
                        </svg>
                        <div className="mt-2 flex items-center justify-between px-1">
                          <span className="text-[10px] font-semibold tracking-[0.12em] uppercase text-kin-muted">
                            4w ago
                          </span>
                          <span className="text-[10px] font-semibold tracking-[0.12em] uppercase text-kin-muted">
                            Today
                          </span>
                        </div>
                      </div>
                    </article>

                    <div className="grid grid-cols-3 gap-4">
                      <article className="kin-surface-strong rounded-[20px] p-4">
                        <p className="text-xs font-semibold tracking-[0.12em] uppercase text-kin-muted">
                          Readiness Dial
                        </p>
                        <div className="mt-2 flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold text-kin-black">82 / 100</p>
                            <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-kin-green-dark">
                              optimal
                            </p>
                          </div>
                          <MiniGauge value={82} />
                        </div>

                        <div className="mt-3 border-t border-kin-sage/20 pt-3">
                          <p className="text-xs font-semibold tracking-[0.12em] uppercase text-kin-muted">
                            Lab States
                          </p>
                          <div className="mt-2 space-y-2">
                            <div className="flex items-center justify-between rounded-[8px] border border-kin-sage/15 bg-[rgba(254,253,251,0.45)] px-3 py-2 shadow-[0_1px_2px_rgba(0,0,0,0.02)] backdrop-blur-md">
                              <span className="text-xs text-kin-body">Vitamin D 18</span>
                              <span className="text-[10px] font-semibold text-kin-orange">
                                Attention
                              </span>
                            </div>
                            <div className="flex items-center justify-between rounded-[8px] border border-kin-sage/15 bg-[rgba(254,253,251,0.45)] px-3 py-2 shadow-[0_1px_2px_rgba(0,0,0,0.02)] backdrop-blur-md">
                              <span className="text-xs text-kin-body">HbA1c 5.8%</span>
                              <span className="text-[10px] font-semibold text-kin-gold-dark">
                                Review
                              </span>
                            </div>
                          </div>
                        </div>
                      </article>

                      <article className="kin-surface-strong rounded-[20px] p-4">
                        <p className="text-xs font-semibold tracking-[0.12em] uppercase text-kin-muted">
                          Range
                        </p>
                        <p className="mt-2 text-sm font-semibold text-kin-black">74% in target</p>
                        <div className="mt-3">
                          <div className="relative h-2 rounded-full bg-kin-olive/20">
                            <div
                              className="absolute inset-y-0 rounded-full bg-kin-green/30"
                              style={{
                                left: `${miniRangeState.low}%`,
                                right: `${100 - miniRangeState.high}%`,
                              }}
                            />
                            <span
                              className="absolute top-1/2 h-3 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-kin-green-dark shadow-sm"
                              style={{ left: `${miniRangeState.current}%` }}
                            />
                          </div>
                          <div className="mt-2 flex items-center justify-between text-[10px] font-semibold tracking-[0.08em] uppercase text-kin-muted">
                            <span>Low</span>
                            <span>Target</span>
                            <span>High</span>
                          </div>
                        </div>

                        <div className="mt-3 border-t border-kin-sage/20 pt-3">
                          <p className="text-xs font-semibold tracking-[0.12em] uppercase text-kin-muted">
                            Daily Inputs
                          </p>
                          <div className="mt-2 space-y-3">
                            {[
                              { label: "Nutrition", value: 86, tone: "bg-kin-green" },
                              { label: "Medication", value: 100, tone: "bg-kin-lavender" },
                              { label: "Mobility", value: 64, tone: "bg-kin-gold" },
                            ].map((item) => (
                              <div key={item.label}>
                                <div className="mb-1 flex items-center justify-between">
                                  <span className="text-xs text-kin-body">{item.label}</span>
                                  <span className="text-[10px] font-semibold text-kin-muted">
                                    {item.value}%
                                  </span>
                                </div>
                                <div className="h-2 rounded-full bg-kin-olive/20 overflow-hidden shadow-inner">
                                  <div
                                    className={`h-full rounded-full ${item.tone}`}
                                    style={{ width: `${item.value}%` }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </article>

                      <article className="kin-surface-strong rounded-[20px] p-4">
                        <p className="text-xs font-semibold tracking-[0.12em] uppercase text-kin-muted">
                          Sparkline
                        </p>
                        <p className="mt-2 text-sm font-semibold text-kin-black">Recovery trend</p>
                        <div className="mt-2">
                          <svg viewBox="0 0 130 34" className="h-8 w-full">
                            <path
                              d={drawArea(miniTrendSpark, 130, 34, 2, 58, 70)}
                              fill="rgba(52,199,89,0.1)"
                            />
                            <path
                              d={drawLine(miniTrendSpark, 130, 34, 2, 58, 70)}
                              fill="none"
                              stroke="rgba(52,199,89,0.9)"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>

                        <div className="mt-3 border-t border-kin-sage/20 pt-3">
                          <p className="text-xs font-semibold tracking-[0.12em] uppercase text-kin-muted">
                            09 Gradients
                          </p>
                          <div className="mt-2 grid grid-cols-3 gap-2">
                            <div className="h-12 rounded-[8px] border border-kin-olive/10 bg-kin-sunrise" />
                            <div className="h-12 rounded-[8px] border border-kin-olive/10 bg-kin-waves" />
                            <div className="h-12 rounded-[8px] border border-kin-olive/10 bg-kin-splash" />
                          </div>
                        </div>
                      </article>
                    </div>
                  </div>

                  <div className="col-span-4 flex min-h-0 flex-col gap-4">
                    <article className="kin-surface-strong rounded-[20px] p-4">
                      <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
                        04 Components
                      </p>
                      <div className="mt-3 space-y-2">
                        <button
                          type="button"
                          className="w-full rounded-[12px] bg-kin-green px-3 py-2 text-xs font-semibold text-white shadow-[0_2px_8px_rgba(52,199,89,0.3)] transition hover:opacity-90"
                        >
                          Primary Button
                        </button>
                        <div className="rounded-[12px] border border-kin-olive/20 bg-kin-white px-3 py-2 text-xs text-kin-muted shadow-sm inset-shadow-sm">
                          Input field
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                          <span className="rounded-full border border-kin-green/20 bg-kin-green/10 px-3 py-1 text-[10px] font-semibold text-kin-green-dark">
                            Normal
                          </span>
                          <span className="rounded-full border border-kin-gold/30 bg-kin-gold/20 px-3 py-1 text-[10px] font-semibold text-kin-gold-dark">
                            Review
                          </span>
                        </div>
                      </div>
                    </article>

                    <article className="kin-surface-strong flex-1 rounded-[24px] p-4 min-h-0">
                      <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
                        08 AI Conversation
                      </p>
                      <div className="mt-4 space-y-3">
                        <div className="rounded-[16px] rounded-bl-sm border border-kin-white/40 bg-kin-lavender/10 px-3 py-2 shadow-sm backdrop-blur-md">
                          <p className="text-xs text-kin-body/90">
                            Recovery improved +15 points in 3 weeks.
                          </p>
                        </div>
                        <div className="rounded-[16px] rounded-br-sm border border-kin-sage/20 bg-[rgba(254,253,251,0.6)] px-3 py-2 shadow-sm backdrop-blur-md ml-4">
                          <p className="text-xs text-kin-body/90">
                            Could Vitamin D explain my low energy?
                          </p>
                        </div>
                        <div className="rounded-[16px] rounded-bl-sm border border-kin-white/40 bg-kin-lavender/10 px-3 py-2 shadow-sm backdrop-blur-md">
                          <p className="text-xs text-kin-body/90">
                            Likely contributor. Also review sleep consistency.
                          </p>
                        </div>
                      </div>
                      <div className="mt-4 rounded-[12px] border border-kin-olive/20 bg-kin-white px-3 py-2 text-xs text-kin-muted shadow-inner">
                        Ask about trends, labs, and next steps...
                      </div>
                    </article>

                    <article className="kin-surface-strong rounded-[20px] p-4">
                      <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
                        System Map
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {sectionChips.map((chip) => (
                          <span
                            key={chip}
                            className="rounded-full border border-kin-sage/18 bg-[rgba(254,253,251,0.45)] px-3 py-1 text-[10px] font-semibold tracking-[0.08em] uppercase text-kin-muted shadow-[0_1px_2px_rgba(0,0,0,0.03)] backdrop-blur-sm"
                          >
                            {chip}
                          </span>
                        ))}
                      </div>
                    </article>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
